import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Package, CheckCircle2, Truck, MapPin, Clock, ChevronRight, Phone } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { CartSidebar } from '../components/CartSidebar';
import { CartItem, Page } from '../App';
const trackingSteps = [{
  id: 1,
  label: 'Order Placed',
  icon: Package,
  description: 'Your order has been received'
}, {
  id: 2,
  label: 'Payment Confirmed',
  icon: CheckCircle2,
  description: 'Payment verified successfully'
}, {
  id: 3,
  label: 'Processing',
  icon: Clock,
  description: 'Order is being prepared'
}, {
  id: 4,
  label: 'Shipped',
  icon: Truck,
  description: 'Package dispatched from Benin City'
}, {
  id: 5,
  label: 'Out for Delivery',
  icon: MapPin,
  description: 'Driver is on the way'
}, {
  id: 6,
  label: 'Delivered',
  icon: CheckCircle2,
  description: 'Package delivered successfully'
}];
const mockOrders: Record<string, {
  status: number;
  items: string;
  date: string;
  eta: string;
  location: string;
}> = {
  'LXC-001': {
    status: 5,
    items: 'Redmi Note 15 Pro × 1',
    date: 'Feb 20, 2025',
    eta: 'Today, 4:00 PM',
    location: 'Benin City, Edo State'
  },
  'LXC-002': {
    status: 3,
    items: 'Luxury Leather Tote × 1, Gold Chain Necklace × 1',
    date: 'Feb 21, 2025',
    eta: 'Feb 24, 2025',
    location: 'Lagos, Lagos State'
  },
  'LXC-003': {
    status: 6,
    items: 'Smart TV 55" × 1',
    date: 'Feb 15, 2025',
    eta: 'Delivered',
    location: 'Abuja, FCT'
  }
};
interface PageProps {
  onAddToCart: (product: Omit<CartItem, 'quantity'>) => void;
  onNavigate: (page: Page) => void;
  cartItems: CartItem[];
  cartCount: number;
  isCartOpen: boolean;
  onOpenCart: () => void;
  onCloseCart: () => void;
  onUpdateQuantity: (id: string, delta: number) => void;
  onRemoveItem: (id: string) => void;
}
export function TrackOrderPage({
  onNavigate,
  cartItems,
  cartCount,
  isCartOpen,
  onOpenCart,
  onCloseCart,
  onUpdateQuantity,
  onRemoveItem
}: PageProps) {
  const [orderId, setOrderId] = useState('');
  const [email, setEmail] = useState('');
  const [result, setResult] = useState<(typeof mockOrders)[string] | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const handleTrack = () => {
    if (!orderId || !email) return;
    setIsLoading(true);
    setNotFound(false);
    setResult(null);
    setTimeout(() => {
      const order = mockOrders[orderId.toUpperCase()];
      if (order) {
        setResult(order);
      } else {
        setNotFound(true);
      }
      setIsLoading(false);
    }, 1200);
  };
  return <div className="bg-[#0F0F0F] min-h-screen text-white" data-id="element-590">
      <Navbar onOpenCart={onOpenCart} cartCount={cartCount} onNavigate={onNavigate} data-id="element-591" />

      <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto" data-id="element-592">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-8" data-id="element-593">
          <button onClick={() => onNavigate('home')} className="hover:text-[#D4AF37] transition-colors" data-id="element-594">
            Home
          </button>
          <ChevronRight className="w-4 h-4" data-id="element-595" />
          <span className="text-[#D4AF37]" data-id="element-596">Track Order</span>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} data-id="element-597">
          <h1 className="text-4xl md:text-5xl font-bold font-serif text-white mb-3" data-id="element-598">
            Track Your Order
          </h1>
          <p className="text-gray-400 mb-10" data-id="element-599">
            Enter your order ID and email to see real-time delivery status.
          </p>
        </motion.div>

        {/* Search Form */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="glass-panel rounded-2xl p-6 md:p-8 border border-white/10 mb-8" data-id="element-600">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4" data-id="element-601">
            <div data-id="element-602">
              <label className="block text-sm font-medium text-gray-400 mb-2" data-id="element-603">
                Order ID
              </label>
              <input type="text" placeholder="e.g. LXC-001" value={orderId} onChange={e => setOrderId(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleTrack()} className="w-full bg-white/5 border border-white/10 rounded-lg py-3 px-4 text-white placeholder-gray-600 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors" data-id="element-604" />
            </div>
            <div data-id="element-605">
              <label className="block text-sm font-medium text-gray-400 mb-2" data-id="element-606">
                Email Address
              </label>
              <input type="email" placeholder="your@email.com" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleTrack()} className="w-full bg-white/5 border border-white/10 rounded-lg py-3 px-4 text-white placeholder-gray-600 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors" data-id="element-607" />
            </div>
          </div>
          <button onClick={handleTrack} disabled={isLoading || !orderId || !email} className="w-full flex items-center justify-center gap-2 py-4 bg-[#D4AF37] text-black font-bold rounded-lg hover:bg-[#c4a030] transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-608">
            {isLoading ? <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" data-id="element-609" /> : <>
                <Search className="w-5 h-5" data-id="element-610" />
                Track Order
              </>}
          </button>
          <p className="text-center text-xs text-gray-600 mt-3" data-id="element-611">
            Try: Order ID <span className="text-[#D4AF37]" data-id="element-612">LXC-001</span> with
            any email
          </p>
        </motion.div>

        {/* Not Found */}
        <AnimatePresence data-id="element-613">
          {notFound && <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} className="glass-panel rounded-xl p-6 border border-red-500/20 text-center mb-8" data-id="element-614">
              <p className="text-red-400 font-medium" data-id="element-615">Order not found</p>
              <p className="text-gray-500 text-sm mt-1" data-id="element-616">
                Please check your order ID and email address.
              </p>
            </motion.div>}
        </AnimatePresence>

        {/* Result */}
        <AnimatePresence data-id="element-617">
          {result && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} className="space-y-6" data-id="element-618">
              {/* Order Info */}
              <div className="glass-panel rounded-2xl p-6 border border-[#D4AF37]/20" data-id="element-619">
                <div className="flex items-start justify-between mb-4" data-id="element-620">
                  <div data-id="element-621">
                    <p className="text-[#D4AF37] text-sm font-medium uppercase tracking-wider" data-id="element-622">
                      Order #{orderId.toUpperCase()}
                    </p>
                    <p className="text-white font-semibold mt-1" data-id="element-623">
                      {result.items}
                    </p>
                    <p className="text-gray-500 text-sm mt-1" data-id="element-624">
                      Placed: {result.date}
                    </p>
                  </div>
                  <div className="text-right" data-id="element-625">
                    <p className="text-gray-400 text-xs" data-id="element-626">ETA</p>
                    <p className="text-[#00FF87] font-semibold text-sm" data-id="element-627">
                      {result.eta}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-gray-400 text-sm" data-id="element-628">
                  <MapPin className="w-4 h-4 text-[#D4AF37]" data-id="element-629" />
                  {result.location}
                </div>
              </div>

              {/* Timeline */}
              <div className="glass-panel rounded-2xl p-6 border border-white/10" data-id="element-630">
                <h3 className="text-white font-semibold mb-6" data-id="element-631">
                  Delivery Timeline
                </h3>
                <div className="space-y-0" data-id="element-632">
                  {trackingSteps.map((step, index) => {
                const isCompleted = step.id <= result.status;
                const isCurrent = step.id === result.status;
                const isLast = index === trackingSteps.length - 1;
                return <div key={step.id} className="flex gap-4" data-id="element-633">
                        <div className="flex flex-col items-center" data-id="element-634">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${isCurrent ? 'bg-[#D4AF37] text-black ring-4 ring-[#D4AF37]/20' : isCompleted ? 'bg-[#00FF87]/20 text-[#00FF87]' : 'bg-white/5 text-gray-600'}`} data-id="element-635">
                            <step.icon className="w-4 h-4" data-id="element-636" />
                          </div>
                          {!isLast && <div className={`w-0.5 h-8 mt-1 ${isCompleted ? 'bg-[#00FF87]/40' : 'bg-white/10'}`} data-id="element-637" />}
                        </div>
                        <div className="pb-8" data-id="element-638">
                          <p className={`font-medium text-sm ${isCurrent ? 'text-[#D4AF37]' : isCompleted ? 'text-white' : 'text-gray-600'}`} data-id="element-639">
                            {step.label}
                            {isCurrent && <span className="ml-2 text-xs bg-[#D4AF37]/20 text-[#D4AF37] px-2 py-0.5 rounded-full" data-id="element-640">
                                Current
                              </span>}
                          </p>
                          <p className={`text-xs mt-0.5 ${isCompleted ? 'text-gray-400' : 'text-gray-700'}`} data-id="element-641">
                            {step.description}
                          </p>
                        </div>
                      </div>;
              })}
                </div>
              </div>

              {/* Support */}
              <div className="glass-panel rounded-xl p-4 border border-white/5 flex items-center justify-between" data-id="element-642">
                <div data-id="element-643">
                  <p className="text-white text-sm font-medium" data-id="element-644">
                    Need help with your order?
                  </p>
                  <p className="text-gray-500 text-xs" data-id="element-645">
                    Our support team is available 8am–8pm
                  </p>
                </div>
                <a href="tel:+2348000000000" className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-[#D4AF37]/10 border border-white/10 hover:border-[#D4AF37]/30 text-[#D4AF37] rounded-lg transition-colors text-sm font-medium" data-id="element-646">
                  <Phone className="w-4 h-4" data-id="element-647" />
                  Call Support
                </a>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      <Footer onNavigate={onNavigate} data-id="element-648" />
      <CartSidebar isOpen={isCartOpen} onClose={onCloseCart} items={cartItems} onUpdateQuantity={onUpdateQuantity} onRemoveItem={onRemoveItem} onNavigate={onNavigate} data-id="element-649" />
    </div>;
}